import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { isPortalHost, isStaffPortalHost } from './portal/portalApi';
import { companyInAddress } from './utils/companyAddress';
import CompanyAddressGuard from './components/CompanyAddressGuard';
import FindPortal from './components/FindPortal';
import { AuthProvider } from './contexts/AuthContext';
import { ToastProvider } from './contexts/ToastContext';
import { SocketProvider } from './contexts/SocketContext';
import { ThemeProvider } from './contexts/ThemeContext';
import ErrorBoundary from './components/ErrorBoundary';
import ProtectedRoute from './components/ProtectedRoute';

// Core pages loaded immediately (small, frequently accessed)
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';

// Lazy load heavy components
const PricingPage = lazy(() => import('./pages/PricingPage'));
const PortalApp = lazy(() => import('./portal/PortalApp'));
const PortalAccessDecision = lazy(() => import('./pages/PortalAccessDecision'));
const StaffPortalRedirect = lazy(() => import('./components/StaffPortalRedirect'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const CustomerDashboard = lazy(() => import('./pages/CustomerDashboard'));
const AgentDashboard = lazy(() => import('./pages/AgentDashboard'));
const SimpleAdminDashboard = lazy(() => import('./pages/SimpleAdminDashboard'));
const FieldLayout = lazy(() => import('./pages/FieldLayout'));
const ModuleSettings = lazy(() => import('./pages/ModuleSettings'));
const Billing = lazy(() => import('./pages/Billing'));
const ModuleRecords = lazy(() => import('./pages/ModuleRecords'));
const ModuleRecordPage = lazy(() => import('./pages/ModuleRecordPage'));
const CreateTicketPage = lazy(() => import('./pages/CreateTicketPage'));
const TestAPI = lazy(() => import('./TestAPI'));
const TestTicketForm = lazy(() => import('./pages/TestTicketForm'));
const ColorPickerDemo = lazy(() => import('./pages/ColorPickerDemo'));
const AdminCustomerProvisioning = lazy(() => import('./pages/AdminCustomerProvisioning'));
const SuperAdminDashboard = lazy(() => import('./pages/SuperAdminDashboard'));
const BSIAdminLogin = lazy(() => import('./pages/BSIAdminLogin'));
const CheckEmailPage = lazy(() => import('./pages/account/CheckEmailPage'));
const VerifyEmailPage = lazy(() => import('./pages/account/VerifyEmailPage'));
const ForgotPasswordPage = lazy(() => import('./pages/account/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/account/ResetPasswordPage'));
const DataManagement = lazy(() => import('./pages/DataManagement'));
const SQLQueryBuilder = lazy(() => import('./pages/SQLQueryBuilder'));
const CustomerReports = lazy(() => import('./pages/CustomerReports'));
const VisualReportBuilder = lazy(() => import('./pages/VisualReportBuilder'));
const AdminSettings = lazy(() => import('./pages/AdminSettings'));
const CompanyProfile = lazy(() => import('./pages/CompanyProfile'));
const Rebranding = lazy(() => import('./pages/Rebranding'));
const BusinessHours = lazy(() => import('./pages/BusinessHours'));
const EmailSettings = lazy(() => import('./pages/EmailSettings'));
const HolidayList = lazy(() => import('./pages/HolidayList'));
const Departments = lazy(() => import('./pages/Departments'));
const CustomerHappiness = lazy(() => import('./pages/CustomerHappiness'));
const Agents = lazy(() => import('./pages/Agents'));
const Teams = lazy(() => import('./pages/Teams'));
const OrganizationalRoles = lazy(() => import('./pages/OrganizationalRoles'));
const Profiles = lazy(() => import('./pages/Profiles'));
const Products = lazy(() => import('./pages/Products'));
const AssignmentRules = lazy(() => import('./pages/AssignmentRules'));
const Macros = lazy(() => import('./pages/Macros'));
const Gamification = lazy(() => import('./pages/Gamification'));
const TicketStatusManagement = lazy(() => import('./pages/TicketStatusManagement'));
const AgentCreateTicketForm = lazy(() => import('./components/AgentCreateTicketForm'));
const TicketDetailPage = lazy(() => import('./pages/TicketDetailPage'));
const AgentAccountsList = lazy(() => import('./pages/AgentAccountsList'));
const AgentCustomersList = lazy(() => import('./pages/AgentCustomersList'));
const AgentAccountDetail = lazy(() => import('./pages/AgentAccountDetail'));
const AgentCustomerDetail = lazy(() => import('./pages/AgentCustomerDetail'));
const SimpleTicketTest = lazy(() => import('./pages/SimpleTicketTest'));

// Loading component
const LoadingSpinner = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
  </div>
);

function App() {
  const location = useLocation();
  // A customer portal: help.bomizzel.com/<name>, or /portal/<name> here.
  // It has its own sign-in and look, so none of the app's providers.
  if (isPortalHost() || location.pathname.startsWith('/portal/')) {
    return (
      <ErrorBoundary>
        <Suspense fallback={<LoadingSpinner />}>
          <Routes>
            <Route path={isPortalHost() ? '/:slug/*' : '/portal/:slug/*'} element={<PortalApp />} />
            <Route
              path="*"
              element={
                isPortalHost() ? (
                  <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
                    <FindPortal className="w-full max-w-md rounded-lg border border-gray-200 bg-white p-6 shadow-sm" />
                  </div>
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />
          </Routes>
        </Suspense>
      </ErrorBoundary>
    );
  }

  return (
    <ThemeProvider>
      <AuthProvider>
        {/*
          Neither of these was mounted. useToast and useSocket both throw when
          their provider is missing, and CustomerDashboard calls
          useRealTimeNotifications on its first line, which uses both - so the
          customer dashboard threw before it rendered anything. AgentDashboard
          does not use those hooks, which is why this went unnoticed.
        */}
        <ToastProvider>
          <SocketProvider>
            <div className="App">
              <CompanyAddressGuard />
              <ErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                  <Routes>
                    {/* Public Routes */}
                    <Route
                      path="/"
                      element={
                        isStaffPortalHost() ? <Navigate to="/login" replace /> : <HomePage />
                      }
                    />
                    {/* portal.bomizzel.com/<name>/...: old customer portal links */}
                    {isStaffPortalHost() && companyInAddress() && (
                      <>
                        <Route path="/tickets/*" element={<StaffPortalRedirect />} />
                        <Route path="/signup" element={<StaffPortalRedirect />} />
                      </>
                    )}
                    <Route path="/pricing" element={<PricingPage />} />
                    <Route path="/privacy" element={<PrivacyPage />} />

                    {/* Customer Routes - For customers using the ticketing system */}
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/check-email" element={<CheckEmailPage />} />
                    <Route path="/verify-email" element={<VerifyEmailPage />} />
                    <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                    <Route path="/reset-password" element={<ResetPasswordPage />} />
                    <Route path="/register" element={<RegisterPage />} />
                    {/* An admin's Approve / Deny link for portal access */}
                    <Route path="/portal-access" element={<PortalAccessDecision />} />
                    {/* One sign-up page; old links to the long form go there */}
                    <Route path="/company-register" element={<Navigate to="/register" replace />} />
                    <Route
                      path="/customer"
                      element={
                        <ProtectedRoute requiredRole="customer">
                          <CustomerDashboard />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/agent"
                      element={
                        <ProtectedRoute requiredRole="employee">
                          <AgentDashboard />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/agent/tickets/create"
                      element={
                        <ProtectedRoute requiredRole="employee">
                          <AgentCreateTicketForm />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/agent/tickets/:ticketId"
                      element={
                        <ProtectedRoute requiredRole="employee">
                          <TicketDetailPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/agent/modules/:moduleKey"
                      element={
                        <ProtectedRoute requiredRole="employee">
                          <ModuleRecords />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/agent/modules/:moduleKey/:recordId"
                      element={
                        <ProtectedRoute requiredRole="employee">
                          <ModuleRecordPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/settings/billing"
                      element={
                        <ProtectedRoute requiredRole="admin">
                          <Billing />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/settings/modules"
                      element={
                        <ProtectedRoute requiredRole="admin">
                          <ModuleSettings />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/agent/accounts"
                      element={
                        <ProtectedRoute requiredRole="employee">
                          <AgentAccountsList />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/agent/customers"
                      element={
                        <ProtectedRoute requiredRole="employee">
                          <AgentCustomersList />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/agent/accounts/:accountId"
                      element={
                        <ProtectedRoute requiredRole="employee">
                          <AgentAccountDetail />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/agent/customers/:customerId"
                      element={
                        <ProtectedRoute requiredRole="employee">
                          <AgentCustomerDetail />
                        </ProtectedRoute>
                      }
                    />
                    {/* Redirect old employee URL to new agent URL */}
                    <Route path="/employee" element={<Navigate to="/agent" replace />} />
                    <Route
                      path="/admin"
                      element={
                        <ProtectedRoute requiredRole="admin">
                          <AdminSettings />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/old-dashboard"
                      element={
                        <ProtectedRoute requiredRole="admin">
                          <SimpleAdminDashboard />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/layouts"
                      element={
                        <ProtectedRoute requiredRole="admin">
                          <FieldLayout />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/layouts/:module"
                      element={
                        <ProtectedRoute requiredRole="admin">
                          <FieldLayout />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/create-ticket"
                      element={
                        <ProtectedRoute>
                          <CreateTicketPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/data-management"
                      element={
                        <ProtectedRoute requiredRole="admin">
                          <DataManagement />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/reports"
                      element={
                        <ProtectedRoute requiredRole="admin">
                          <CustomerReports />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/visual-reports"
                      element={
                        <ProtectedRoute requiredRole="admin">
                          <VisualReportBuilder />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/settings/*"
                      element={
                        <ProtectedRoute requiredRole="admin">
                          <AdminSettings />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/settings/company-profile"
                      element={
                        <ProtectedRoute requiredRole="admin">
                          <CompanyProfile />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/settings/rebranding"
                      element={
                        <ProtectedRoute requiredRole="admin">
                          <Rebranding />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/settings/email"
                      element={
                        <ProtectedRoute requiredRole="admin">
                          <EmailSettings />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/settings/business-hours"
                      element={
                        <ProtectedRoute requiredRole="admin">
                          <BusinessHours />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/settings/holidays"
                      element={
                        <ProtectedRoute requiredRole="admin">
                          <HolidayList />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/settings/departments"
                      element={
                        <ProtectedRoute requiredRole="admin">
                          <Departments />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/settings/customer-happiness"
                      element={
                        <ProtectedRoute requiredRole="admin">
                          <CustomerHappiness />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/settings/agents"
                      element={
                        <ProtectedRoute requiredRole="admin">
                          <Agents />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/settings/teams"
                      element={
                        <ProtectedRoute requiredRole="admin">
                          <Teams />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/settings/roles"
                      element={
                        <ProtectedRoute requiredRole="admin">
                          <OrganizationalRoles />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/settings/profiles"
                      element={
                        <ProtectedRoute requiredRole="admin">
                          <Profiles />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/settings/products"
                      element={
                        <ProtectedRoute requiredRole="admin">
                          <Products />
                        </ProtectedRoute>
                      }
                    />
                    {/* Admins manage shared macros here; agents reach it from a ticket */}
                    <Route
                      path="/admin/settings/macros"
                      element={
                        <ProtectedRoute requiredRole="admin">
                          <Macros />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/macros"
                      element={
                        <ProtectedRoute requiredRole="employee">
                          <Macros />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/settings/assignment-rules"
                      element={
                        <ProtectedRoute requiredRole="admin">
                          <AssignmentRules />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/settings/game-scope"
                      element={
                        <ProtectedRoute requiredRole="admin">
                          <Gamification />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/settings/ticket-statuses"
                      element={
                        <ProtectedRoute requiredRole="admin">
                          <TicketStatusManagement />
                        </ProtectedRoute>
                      }
                    />

                    {/* BSI Admin Routes - For Jeff to manage Bomizzel customers */}
                    <Route path="/bsi/login" element={<BSIAdminLogin />} />
                    <Route
                      path="/bsi/dashboard"
                      element={
                        <ProtectedRoute requireBSI={true}>
                          <SuperAdminDashboard />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/bsi/provisioning"
                      element={
                        <ProtectedRoute requireBSI={true}>
                          <AdminCustomerProvisioning />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/bsi/query-builder"
                      element={
                        <ProtectedRoute requireBSI={true}>
                          <SQLQueryBuilder />
                        </ProtectedRoute>
                      }
                    />

                    {/* Test Routes */}
                    <Route path="/test-ticket-form" element={<TestTicketForm />} />
                    <Route path="/color-picker-demo" element={<ColorPickerDemo />} />
                    <Route path="/test" element={<TestAPI />} />
                    <Route
                      path="/simple-ticket-test"
                      element={
                        <ProtectedRoute requiredRole="employee">
                          <SimpleTicketTest />
                        </ProtectedRoute>
                      }
                    />
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </Suspense>
              </ErrorBoundary>
            </div>
          </SocketProvider>
        </ToastProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
